import React from 'react';
import axios from 'axios';
import TextInput from '../../components/TextInput';
import SelectBox from '../../components/SelectBox';
import Button from '../../components/Button';

// Data & Backups — nightly pg_dump of the C2C database, pruned locally with an
// optional copy to a mounted network share (NAS). Self-contained: it talks to
// the /api/backup-* endpoints directly (separate from the main Settings form),
// so saving here never touches the syslog/email settings and vice versa.
// Provider-only on the backend; renders nothing if the caller isn't allowed.

const fmtBytes = (n) => {
	if (n === null || n === undefined) return '—';
	const u = ['B', 'KB', 'MB', 'GB', 'TB'];
	let v = Number(n), i = 0;
	while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
	return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtWhen = (iso) => {
	if (!iso) return '—';
	try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' };
const valueCell = { fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)' };

const BackupSettings = ({ styles }) => {
	const [form, setForm] = React.useState(null);
	const [info, setInfo] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [busy, setBusy] = React.useState(false);
	const [toast, setToast] = React.useState({ type: '', message: '' });

	const load = React.useCallback(async () => {
		try {
			const { data } = await axios.get('/api/backup-settings');
			if (data && data.status) {
				setForm({
					BackupEnabled: !!data.BackupEnabled,
					BackupHour: String(data.BackupHour ?? '2'),
					BackupKeep: String(data.BackupKeep ?? '14'),
					BackupLocalPath: data.BackupLocalPath || '',
					BackupExcludeTelemetry: !!data.BackupExcludeTelemetry,
					BackupOffsite: data.BackupOffsite || 'none',
					BackupFolderPath: data.BackupFolderPath || '',
					BackupOffsitePrefix: data.BackupOffsitePrefix || '',
				});
				setInfo(data.statusInfo || null);
			}
		} catch (e) { /* not a provider, or request failed — render nothing */ }
		setLoading(false);
	}, []);

	React.useEffect(() => { load(); }, [load]);

	const setStr = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
	const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === 'true' }));

	const body = () => ({ ...form });

	const post = React.useCallback(async (url, payload, okMsg) => {
		setBusy(true);
		setToast({ type: '', message: '' });
		try {
			const { data } = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
			if (data && data.status) {
				setToast({ type: 'success', message: data.msg || okMsg });
			} else {
				setToast({ type: 'error', message: (data && data.msg) || 'Request failed' });
			}
		} catch (e) {
			setToast({ type: 'error', message: e?.response?.data?.msg || e?.message || 'Request failed' });
		}
		await load();
		setBusy(false);
	}, [load]);

	if (loading || !form) return null;

	const last = info?.backup_last;
	const pgMissing = info && info.pg_dump_available === false;

	return (
		<div className={styles?.content} style={{ marginTop: 28, borderTop: '1px solid var(--border-color)', paddingTop: 20 }}>
			<h2 style={{ marginBottom: 4 }}>Data &amp; Backups</h2>
			<p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 0 }}>
				Nightly <code>pg_dump</code> of the C2C database to a local folder, pruned to the newest copies,
				with an optional copy to a mounted network share.
			</p>

			{pgMissing && (
				<div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8,
					background: 'rgba(192,20,18,.1)', border: '1px solid rgba(192,20,18,.35)', color: 'var(--text-primary)' }}>
					<strong>pg_dump not found</strong> on the server — install the PostgreSQL client
					(<code>postgresql-client</code>). Backups cannot run until it is present.
				</div>
			)}

			{/* Status summary */}
			<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, margin: '14px 0' }}>
				<div>
					<div style={label}>Last backup</div>
					<div style={valueCell}>{fmtWhen(last?.at)}</div>
					<div style={{ fontSize: 12, color: last ? (last.ok ? 'var(--green,#12c077)' : 'var(--primary-color)') : 'var(--text-secondary)' }}>
						{info?.backup_running ? 'Running now…'
							: last ? (last.ok ? `OK · ${fmtBytes(last.size)}${last.excludedTelemetry ? ' · telemetry excluded' : ''}`
								: `Failed: ${last.error || 'unknown error'}`)
							: 'No backup yet'}
					</div>
				</div>
				<div>
					<div style={label}>Database size</div>
					<div style={valueCell}>{fmtBytes(info?.db_size)}</div>
					<div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{info?.db_name || ''}</div>
				</div>
				<div>
					<div style={label}>Off-site copy</div>
					<div style={valueCell}>{last?.offsite ? (last.offsite.ok ? 'OK' : 'Failed') : (form.BackupOffsite === 'none' ? 'Disabled' : '—')}</div>
					{last?.offsite && !last.offsite.ok && (
						<div style={{ fontSize: 12, color: 'var(--primary-color)' }}>{last.offsite.error}</div>
					)}
				</div>
			</div>

			{/* Config */}
			<div className={styles?.content}>
				<SelectBox label='Nightly backups' name='BackupEnabled' value={String(form.BackupEnabled)} onChange={setBool('BackupEnabled')}>
					<option value='true'>Enabled</option>
					<option value='false'>Disabled</option>
				</SelectBox>
				<TextInput type='number' label='Backup hour (0-23, server time)' value={form.BackupHour} onChange={setStr('BackupHour')} />
				<TextInput type='number' label='Local copies to keep' value={form.BackupKeep} onChange={setStr('BackupKeep')} />
			</div>
			<div className={styles?.content}>
				<TextInput label='Local backup folder' placeholder='/var/backups/watcher-c2c' value={form.BackupLocalPath} onChange={setStr('BackupLocalPath')} />
				<SelectBox label='Raw telemetry data' name='BackupExcludeTelemetry' value={String(form.BackupExcludeTelemetry)} onChange={setBool('BackupExcludeTelemetry')}>
					<option value='true'>Exclude (smaller, recommended)</option>
					<option value='false'>Include (full dump, large)</option>
				</SelectBox>
			</div>
			<p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -4 }}>
				Excluding telemetry skips <code>net_traffic</code>/<code>flows</code> row data (already archived by
				partition-maintenance); the schema and every other table are always included.
			</p>

			<div className={styles?.content}>
				<SelectBox label='Off-site copy' name='BackupOffsite' value={form.BackupOffsite} onChange={setStr('BackupOffsite')}>
					<option value='none'>None (local only)</option>
					<option value='folder'>Mounted network share (NAS)</option>
				</SelectBox>
				{form.BackupOffsite === 'folder' && (
					<>
						<TextInput label='Network folder (mount point)' placeholder='/mnt/watcher-nas' value={form.BackupFolderPath} onChange={setStr('BackupFolderPath')} />
						<TextInput label='Folder prefix' placeholder='watcher-c2c/' value={form.BackupOffsitePrefix} onChange={setStr('BackupOffsitePrefix')} />
					</>
				)}
			</div>

			{/* Actions */}
			<div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
				<Button onClick={() => post('/api/backup-settings', body(), 'Backup settings saved')} disabled={busy}>Save</Button>
				<Button onClick={() => post('/api/backup-run', {}, 'Backup started')} disabled={busy || info?.backup_running}>Back up now</Button>
				{form.BackupOffsite === 'folder' && (
					<Button onClick={() => post('/api/backup-test-offsite', body(), 'Wrote a test file to the network folder')} disabled={busy}>Test network folder</Button>
				)}
			</div>

			{toast.message && (
				<div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8,
					background: toast.type === 'error' ? 'rgba(192,20,18,.1)' : 'rgba(18,192,119,.12)',
					border: `1px solid ${toast.type === 'error' ? 'rgba(192,20,18,.35)' : 'rgba(18,192,119,.4)'}`,
					color: 'var(--text-primary)' }}>
					{toast.message}
				</div>
			)}

			{/* Recent backups */}
			{info?.backups?.length > 0 && (
				<div style={{ marginTop: 18 }}>
					<div style={label}>Local backups ({info.backups.length})</div>
					<table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 13 }}>
						<thead>
							<tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
								<th style={{ padding: '4px 8px' }}>File</th>
								<th style={{ padding: '4px 8px' }}>Size</th>
								<th style={{ padding: '4px 8px' }}>When</th>
							</tr>
						</thead>
						<tbody>
							{info.backups.map((b) => (
								<tr key={b.file} style={{ borderTop: '1px solid var(--border-color)' }}>
									<td style={{ padding: '4px 8px', fontFamily: '"JetBrains Mono", monospace' }}>{b.file}</td>
									<td style={{ padding: '4px 8px', ...valueCell }}>{fmtBytes(b.size)}</td>
									<td style={{ padding: '4px 8px', color: 'var(--text-secondary)' }}>{fmtWhen(b.at)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default BackupSettings;
